/**
 * Firewall rule helpers shared by `ResourceFirewallPanel`.
 *
 * Ported from the client console's `ResourceFirewallTab` (the pure helpers only — the client keeps
 * them beside its component; the admin panel is large enough on its own that they live in their own
 * module). Kept in step with the client copy by hand: there is no shared package between the two
 * frontends. Validation messages are plain English here instead of i18n keys — the admin console has
 * no i18n layer.
 */

import type { CreateFirewallRuleRequest, FirewallRule } from "@/types/admin";

/**
 * True when a rule field means "all" - any address, port or protocol, or no macro. WiCloud has one
 * spelling for it in the console and the API: a blank value. The backend turns it into what the
 * webhook expects (left out on a create, "" on an edit), so nothing here sends "all" itself.
 */
export const isAll = (value?: string | null): boolean =>
  !value || !value.trim();

/** The protocols most rules use, offered first. */
export const COMMON_PROTOCOLS = ["tcp", "udp", "icmp"] as const;

/** The rest of the `proto` values WiService accepts (sys team contract), in its own order. */
export const OTHER_PROTOCOLS = [
  "igmp",
  "ggp",
  "ipencap",
  "st",
  "igp",
  "pup",
  "hmp",
  "xns-idp",
  "rdp",
  "iso-tp4",
  "dccp",
  "xtp",
  "ddp",
  "idpr-cmtp",
  "ipv6",
  "ipv6-route",
  "ipv6-frag",
  "idrp",
  "rsvp",
  "gre",
  "esp",
  "ah",
  "skip",
  "ipv6-icmp",
  "ipv6-nonxt",
  "ipv6-opts",
  "vmtp",
  "eigrp",
  "ospf",
  "ax.25",
  "ipip",
  "etherip",
  "encap",
  "pim",
  "ipcomp",
  "vrrp",
  "l2tp",
  "isis",
  "sctp",
  "fc",
  "mobility-header",
  "udplite",
  "mpls-in-ip",
  "manet",
  "hip",
  "shim6",
  "wesp",
  "rohc",
] as const;

/**
 * The protocols that carry ports. WiService stores a port on any other one (ICMP with port 443 was
 * accepted on dev, 2026-09-18), but the rule then matches nothing the operator meant.
 */
export const PORT_PROTOCOLS: readonly string[] = [
  "tcp",
  "udp",
  "udplite",
  "dccp",
  "sctp",
];

/**
 * Select value for "no protocol" (the rule matches every protocol). Radix Select cannot hold an
 * empty string, so the form maps this to "" and back.
 */
export const ANY_PROTOCOL = "any";

const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const PORT_PATTERN = /^\d{1,5}$/;

const isIpv4 = (value: string): boolean => {
  const match = IPV4_PATTERN.exec(value);
  if (!match) return false;
  return match.slice(1).every((octet) => Number(octet) <= 255);
};

/** One entry of a source/destination list: an IP, a CIDR subnet, or a "<first>-<last>" range. */
const isAddressItem = (item: string): boolean => {
  const subnet = item.split("/");
  if (subnet.length === 2) {
    const prefixIsValid =
      /^\d{1,2}$/.test(subnet[1]) && Number(subnet[1]) <= 32;
    return isIpv4(subnet[0]) && prefixIsValid;
  }

  const range = item.split("-");
  if (range.length === 2) {
    return isIpv4(range[0]) && isIpv4(range[1]);
  }

  return isIpv4(item);
};

/** An IPv4 address as one number, so two addresses compare in address order. */
const ipv4ToNumber = (value: string): number =>
  value.split(".").reduce((total, octet) => total * 256 + Number(octet), 0);

/**
 * True for a "<first>-<last>" range whose first address is above its last. Proxmox refuses
 * "10.0.0.50-10.0.0.1" with "Begin address is greater than End address" (dev webhook,
 * 2026-09-18); a one-address range ("10.0.0.5-10.0.0.5") is accepted.
 */
const isReversedRange = (item: string): boolean => {
  const range = item.split("-");
  if (range.length !== 2) return false;
  return ipv4ToNumber(range[0]) > ipv4ToNumber(range[1]);
};

// Limits Proxmox enforces on top of the formats (dev webhook, 2026-09-18): a source/dest of more
// than 512 characters is refused, and so is a port list of more than 15 numbers, a range counting
// as two.
export const MAX_ADDRESS_LENGTH = 512;
export const MAX_PORT_NUMBERS = 15;

/**
 * The comment cap the API enforces (`FirewallRuleDto.Comment`, MaxLength 500). Held in the input
 * too, so a long note is stopped as it is typed rather than by a 400 after Save.
 */
export const MAX_COMMENT_LENGTH = 500;

/**
 * True for a subnet written from a host address ("10.0.0.5/24"): Proxmox refuses it with
 * "Invalid prefix"; it has to be the subnet's first address ("10.0.0.0/24").
 */
export const subnetHasHostBits = (ip: string, prefix: string): boolean => {
  const bits = Number(prefix);
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  const address = ipv4ToNumber(ip);
  return (address & mask) >>> 0 !== address;
};

/** True when any subnet in an address value is written from a host address. */
export const hasHostBitsSubnet = (value: string): boolean =>
  value.split(",").some((item) => {
    const subnet = item.split("/");
    return (
      subnet.length === 2 &&
      isIpv4(subnet[0]) &&
      subnetHasHostBits(subnet[0], subnet[1])
    );
  });

/** How Proxmox counts a port list: one per port, two per "first:last" range. */
export const countPortNumbers = (value: string): number =>
  value.split(",").reduce((total, item) => total + item.split(":").length, 0);

/** One entry of a port list: a port, or a "<first>:<last>" range with first ≤ last. */
const isPortItem = (item: string): boolean => {
  const bounds = item.split(":");
  if (bounds.length > 2) return false;

  const allNumeric = bounds.every((bound) => PORT_PATTERN.test(bound));
  if (!allNumeric) return false;

  const ports = bounds.map(Number);
  const inRange = ports.every((port) => port >= 1 && port <= 65535);
  if (!inRange) return false;

  return ports.length === 1 || ports[0] <= ports[1];
};

/**
 * An address or port list without spaces - none of these values can contain one, and
 * "10.0.0.1, 10.0.0.2" is how most people type a list. The inputs drop them as they are typed, the
 * checks read the same value, and the backend does the same before sending, so every path judges
 * and sends one form.
 */
export const withoutSpaces = (value: string): string =>
  value.replace(/\s+/g, "");

const SUBNET_HOST_BITS_MESSAGE =
  "A subnet must start at its first address, e.g. 10.0.0.0/24 rather than 10.0.0.5/24.";
const ADDRESS_TOO_LONG_MESSAGE =
  "Too many addresses — at most 512 characters (about 40 addresses). Use a subnet or a range instead.";
const PORT_LIST_TOO_LONG_MESSAGE =
  "At most 15 ports, and a range counts as two. Merge ports into a range, or split the rule.";

const addressError = (value?: string): string | undefined => {
  if (!value || isAll(value)) return undefined;
  const items = withoutSpaces(value).split(",");
  const valid = items.every(isAddressItem);
  if (!valid) {
    return "Enter an IP, a subnet or a range, e.g. 10.0.0.5, 10.0.0.0/24 or 10.0.0.1-10.0.0.50.";
  }
  // Proxmox takes an IP range only on its own: "10.0.0.1-10.0.0.5,10.0.0.9" is refused with
  // "you can't use a range in a list" (dev webhook, 2026-09-18).
  const rangeInList =
    items.length > 1 && items.some((item) => item.includes("-"));
  if (rangeInList) {
    return "An IP range must be entered on its own — it cannot be combined with other addresses in a list.";
  }
  if (isReversedRange(items[0])) {
    return "An IP range must start with the lower address, e.g. 10.0.0.1-10.0.0.50.";
  }
  if (hasHostBitsSubnet(items.join(","))) return SUBNET_HOST_BITS_MESSAGE;
  return items.join(",").length > MAX_ADDRESS_LENGTH
    ? ADDRESS_TOO_LONG_MESSAGE
    : undefined;
};

const portError = (value?: string): string | undefined => {
  if (!value || isAll(value)) return undefined;
  const compact = withoutSpaces(value);
  // The hyphen is the IP-range separator; a port range takes a colon. Named separately because
  // "8000-8080" is the form operators reach for first.
  if (compact.includes("-"))
    return "Write a port range with a colon, e.g. 8000:8080.";
  const valid = compact.split(",").every(isPortItem);
  if (!valid)
    return "Enter a port from 1 to 65535 or a range such as 8000:8080.";
  return countPortNumbers(compact) > MAX_PORT_NUMBERS
    ? PORT_LIST_TOO_LONG_MESSAGE
    : undefined;
};

/** The fields that decide what a rule's ports mean; an edit sends them together or not at all. */
const PORT_GROUP = ["proto", "sport", "dport", "macro"] as const;

/** The other optional fields; an edit sends each one only when it changed. */
const INDEPENDENT_FIELDS = [
  "source",
  "dest",
  "comment",
  "iface",
  "log",
] as const;

type OptionalRuleField =
  | (typeof PORT_GROUP)[number]
  | (typeof INDEPENDENT_FIELDS)[number];

/** Reads a field the way both the form and the rule read back spell "all": blank. */
const fieldValue = (
  rule: CreateFirewallRuleRequest,
  field: OptionalRuleField,
) => rule[field] ?? "";

const portGroupChanged = (
  form: CreateFirewallRuleRequest,
  baseline: CreateFirewallRuleRequest,
) =>
  PORT_GROUP.some(
    (field) => fieldValue(form, field) !== fieldValue(baseline, field),
  );

export type FirewallRuleField = "source" | "dest" | "sport" | "dport" | "proto";

/**
 * The default form for a new rule: enabled, inbound, ACCEPT, every protocol. A blank protocol
 * reaches the webhook as no `proto` at all; typing a port makes one required, so a port rule
 * still has to pick TCP/UDP.
 */
export const DEFAULT_FIREWALL_RULE_FORM: CreateFirewallRuleRequest = {
  type: "in",
  action: "ACCEPT",
  enable: 1,
  source: "",
  sport: "",
  dest: "",
  dport: "",
  iface: "",
  proto: "",
  log: "nolog",
  macro: "",
  comment: "",
};

/**
 * Strips `proto` from a new rule that carries a macro: the macro already defines its own protocol
 * and ports, and WiService expects the protocol to be left out in that case. An edit goes through
 * `buildRuleUpdate` instead.
 */
export const stripProtoWhenMacroSet = (
  rule: CreateFirewallRuleRequest,
): CreateFirewallRuleRequest =>
  !isAll(rule.macro) ? { ...rule, proto: undefined } : rule;

/**
 * The PATCH body for an edit: `type`, `action` and `enable`, plus only the fields the operator
 * changed. WiService keeps every field a PATCH leaves out, so an unchanged field - including an old
 * value today's rules would refuse - passes through untouched, and a field cleared to blank is sent
 * as "" (the backend forwards "" as "reset to all"). The port group goes as a whole, because the
 * protocol requirement is judged on the four together; with a macro, `proto` goes as "" so a
 * protocol the rule had before the macro is cleared.
 */
export const buildRuleUpdate = (
  form: CreateFirewallRuleRequest,
  baseline: CreateFirewallRuleRequest,
): CreateFirewallRuleRequest => {
  const update: CreateFirewallRuleRequest = {
    type: form.type,
    action: form.action,
    enable: form.enable,
  };

  for (const field of INDEPENDENT_FIELDS) {
    const changed = fieldValue(form, field) !== fieldValue(baseline, field);
    if (changed) update[field] = fieldValue(form, field);
  }

  if (portGroupChanged(form, baseline)) {
    for (const field of PORT_GROUP) update[field] = fieldValue(form, field);
    if (!isAll(form.macro)) update.proto = "";
  }

  return update;
};

export type RuleDirectionFilter = "all" | "in" | "out";

/**
 * From this many rules the list gets a search box and an In/Out filter. Below it the whole list
 * fits on screen, and a toolbar would only crowd a VM that has one SSH rule.
 */
export const RULE_TOOLBAR_THRESHOLD = 6;

/**
 * The rules matching the search term and direction. The term is looked for, case-insensitively and
 * without spaces, in every value the row shows - addresses, ports, protocol, macro, action, comment
 * - so "443", "10.0.0" or "ssh" all find what the operator is looking at.
 */
export const filterFirewallRules = (
  rules: FirewallRule[],
  search: string,
  direction: RuleDirectionFilter,
): FirewallRule[] => {
  const term = withoutSpaces(search).toLowerCase();
  return rules.filter((rule) => {
    if (direction !== "all" && rule.type !== direction) return false;
    if (!term) return true;
    const values = [
      rule.source,
      rule.dest,
      rule.sport,
      rule.dport,
      rule.proto,
      rule.macro,
      rule.action,
      rule.comment,
    ];
    // Both sides without spaces, so "web ports" still finds a comment "web ports".
    return values.some(
      (value) => !!value && withoutSpaces(value).toLowerCase().includes(term),
    );
  });
};

/**
 * Checks a rule against the WiService contract (sys team, 2026-09-18) before it is sent, and
 * returns a message per invalid field. The backend applies the same rules; checking here too lets
 * the form say which field is wrong instead of a toast after the round trip. A macro brings its own
 * protocol and ports, so those fields are only checked without one. With a `baseline` (the edit
 * dialog) only what the edit will send is checked: an unchanged field is not sent, so a rule
 * created before these rules existed can still be edited or switched off.
 */
export const validateFirewallRule = (
  rule: CreateFirewallRuleRequest,
  baseline?: CreateFirewallRuleRequest,
): Partial<Record<FirewallRuleField, string>> => {
  const errors: Partial<Record<FirewallRuleField, string>> = {};
  const isSent = (field: "source" | "dest") =>
    !baseline || fieldValue(rule, field) !== fieldValue(baseline, field);

  const sourceError = isSent("source") ? addressError(rule.source) : undefined;
  if (sourceError) errors.source = sourceError;

  const destError = isSent("dest") ? addressError(rule.dest) : undefined;
  if (destError) errors.dest = destError;

  if (baseline && !portGroupChanged(rule, baseline)) return errors;
  if (!isAll(rule.macro)) return errors;

  const sportError = portError(rule.sport);
  if (sportError) errors.sport = sportError;

  const dportError = portError(rule.dport);
  if (dportError) errors.dport = dportError;

  // WiService requires a protocol whenever a port is set without a macro; with no port it may be
  // left out, and the rule then matches every protocol.
  const hasPort = !isAll(rule.sport) || !isAll(rule.dport);
  if (hasPort && isAll(rule.proto)) {
    errors.proto = "Choose a protocol — it is required when a port is set.";
  } else if (hasPort && !PORT_PROTOCOLS.includes(rule.proto ?? "")) {
    errors.proto =
      "This protocol has no ports. Choose TCP, UDP, UDP-Lite, DCCP or SCTP, or clear the ports.";
  }

  return errors;
};

// ------------------------------------------------------------------------------------------------
// Structured inputs (`FirewallValueInput`): source/destination address and source/destination
// port are entered by kind - all, one, a subnet, a range or a list - and written back as the one
// string WiService takes, so `validateFirewallRule` and `buildRuleUpdate` see what they always did.
// A value no kind represents opens as "custom", a plain text box, so an edit never loses it.

const isPort = (value: string): boolean =>
  PORT_PATTERN.test(value) && Number(value) >= 1 && Number(value) <= 65535;

/** An IP or a CIDR subnet - what an address list may hold (Proxmox refuses a range in a list). */
export const isListAddress = (item: string): boolean => {
  const subnet = item.split("/");
  if (subnet.length === 2) {
    return (
      isIpv4(subnet[0]) &&
      /^\d{1,2}$/.test(subnet[1]) &&
      Number(subnet[1]) <= 32
    );
  }
  return isIpv4(item);
};

export { isPortItem };

export type AddressMode =
  | "all"
  | "single"
  | "subnet"
  | "range"
  | "list"
  | "custom";

export interface AddressDraft {
  mode: AddressMode;
  single: string;
  subnetIp: string;
  subnetPrefix: string;
  rangeFrom: string;
  rangeTo: string;
  items: string[];
  pending: string;
  custom: string;
}

const EMPTY_ADDRESS: AddressDraft = {
  mode: "all",
  single: "",
  subnetIp: "",
  subnetPrefix: "24",
  rangeFrom: "",
  rangeTo: "",
  items: [],
  pending: "",
  custom: "",
};

/** Reads a stored value into the kind it is written as; anything else opens as "custom". */
export const parseAddress = (value?: string | null): AddressDraft => {
  const text = withoutSpaces(value ?? "");
  if (!text) return EMPTY_ADDRESS;

  if (text.includes(",")) {
    const items = text.split(",");
    return items.every(isListAddress)
      ? { ...EMPTY_ADDRESS, mode: "list", items }
      : { ...EMPTY_ADDRESS, mode: "custom", custom: text };
  }

  const range = text.split("-");
  if (range.length === 2 && isIpv4(range[0]) && isIpv4(range[1])) {
    return {
      ...EMPTY_ADDRESS,
      mode: "range",
      rangeFrom: range[0],
      rangeTo: range[1],
    };
  }

  const subnet = text.split("/");
  if (subnet.length === 2 && isListAddress(text)) {
    return {
      ...EMPTY_ADDRESS,
      mode: "subnet",
      subnetIp: subnet[0],
      subnetPrefix: subnet[1],
    };
  }

  if (isIpv4(text)) return { ...EMPTY_ADDRESS, mode: "single", single: text };

  return { ...EMPTY_ADDRESS, mode: "custom", custom: text };
};

/** The one string WiService takes for the draft. An unfinished part is sent as it stands. */
export const serializeAddress = (draft: AddressDraft): string => {
  switch (draft.mode) {
    case "all":
      return "";
    case "single":
      return draft.single;
    case "subnet":
      return draft.subnetIp ? `${draft.subnetIp}/${draft.subnetPrefix}` : "";
    case "range":
      return draft.rangeFrom || draft.rangeTo
        ? `${draft.rangeFrom}-${draft.rangeTo}`
        : "";
    case "list":
      return [...draft.items, withoutSpaces(draft.pending)]
        .filter(Boolean)
        .join(",");
    case "custom":
      return withoutSpaces(draft.custom);
  }
};

/**
 * What is wrong with the draft, or undefined when it is complete. A kind chosen but left empty is
 * wrong too - sent as it stands, it would read as "all".
 */
export const addressProblem = (draft: AddressDraft): string | undefined => {
  switch (draft.mode) {
    case "all":
    case "custom":
      // "custom" is judged by `validateFirewallRule` on the text, as before.
      return undefined;
    case "single":
      if (!draft.single) return "Enter an IP address.";
      return isIpv4(draft.single)
        ? undefined
        : "Enter a valid IP address, e.g. 10.0.0.5.";
    case "subnet":
      if (!draft.subnetIp) return "Enter an IP address.";
      if (!isIpv4(draft.subnetIp))
        return "Enter a valid IP address, e.g. 10.0.0.0.";
      return subnetHasHostBits(draft.subnetIp, draft.subnetPrefix)
        ? SUBNET_HOST_BITS_MESSAGE
        : undefined;
    case "range":
      if (!draft.rangeFrom || !draft.rangeTo)
        return "Fill in both ends of the range.";
      if (!isIpv4(draft.rangeFrom) || !isIpv4(draft.rangeTo)) {
        return "Enter a valid IP address at both ends, e.g. 10.0.0.1 to 10.0.0.50.";
      }
      return ipv4ToNumber(draft.rangeFrom) > ipv4ToNumber(draft.rangeTo)
        ? "An IP range must start with the lower address, e.g. 10.0.0.1-10.0.0.50."
        : undefined;
    case "list": {
      const entries = [...draft.items, withoutSpaces(draft.pending)].filter(
        Boolean,
      );
      if (entries.length === 0) return "Add at least one entry.";
      if (entries.some((item) => item.includes("-"))) {
        return "An IP range cannot go in a list — pick IP range instead.";
      }
      if (!entries.every(isListAddress)) {
        return "Every entry must be an IP or a subnet — remove the ones marked in red.";
      }
      if (hasHostBitsSubnet(entries.join(","))) return SUBNET_HOST_BITS_MESSAGE;
      return entries.join(",").length > MAX_ADDRESS_LENGTH
        ? ADDRESS_TOO_LONG_MESSAGE
        : undefined;
    }
  }
};

export type PortMode = "all" | "single" | "range" | "list" | "custom";

export interface PortDraft {
  mode: PortMode;
  single: string;
  rangeFrom: string;
  rangeTo: string;
  items: string[];
  pending: string;
  custom: string;
}

const EMPTY_PORT: PortDraft = {
  mode: "all",
  single: "",
  rangeFrom: "",
  rangeTo: "",
  items: [],
  pending: "",
  custom: "",
};

/** A typed port-list entry as sent: no spaces, and "8000-8080" read as the range it means. */
export const portListEntry = (value: string): string =>
  withoutSpaces(value).replace("-", ":");

export const parsePort = (value?: string | null): PortDraft => {
  const text = withoutSpaces(value ?? "");
  if (!text) return EMPTY_PORT;

  if (text.includes(",")) {
    const items = text.split(",");
    return items.every(isPortItem)
      ? { ...EMPTY_PORT, mode: "list", items }
      : { ...EMPTY_PORT, mode: "custom", custom: text };
  }

  const range = text.split(":");
  if (range.length === 2 && range.every(isPort)) {
    return {
      ...EMPTY_PORT,
      mode: "range",
      rangeFrom: range[0],
      rangeTo: range[1],
    };
  }

  if (isPort(text)) return { ...EMPTY_PORT, mode: "single", single: text };

  return { ...EMPTY_PORT, mode: "custom", custom: text };
};

export const serializePort = (draft: PortDraft): string => {
  switch (draft.mode) {
    case "all":
      return "";
    case "single":
      return draft.single;
    case "range":
      return draft.rangeFrom || draft.rangeTo
        ? `${draft.rangeFrom}:${draft.rangeTo}`
        : "";
    case "list":
      return [...draft.items, portListEntry(draft.pending)]
        .filter(Boolean)
        .join(",");
    case "custom":
      return withoutSpaces(draft.custom);
  }
};

export const portProblem = (draft: PortDraft): string | undefined => {
  switch (draft.mode) {
    case "all":
    case "custom":
      return undefined;
    case "single":
      if (!draft.single) return "Enter a port.";
      return isPort(draft.single)
        ? undefined
        : "A port must be from 1 to 65535.";
    case "range":
      if (!draft.rangeFrom || !draft.rangeTo)
        return "Fill in both ends of the range.";
      if (!isPort(draft.rangeFrom) || !isPort(draft.rangeTo)) {
        return "A port must be from 1 to 65535.";
      }
      return Number(draft.rangeFrom) > Number(draft.rangeTo)
        ? "The first port must not be above the last."
        : undefined;
    case "list": {
      const entries = [...draft.items, portListEntry(draft.pending)].filter(
        Boolean,
      );
      if (entries.length === 0) return "Add at least one entry.";
      if (!entries.every(isPortItem)) {
        return "Every entry must be a port or a range such as 8000:8080 — remove the ones marked in red.";
      }
      return countPortNumbers(entries.join(",")) > MAX_PORT_NUMBERS
        ? PORT_LIST_TOO_LONG_MESSAGE
        : undefined;
    }
  }
};
